import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { orchard } from '../../theme';
import { prefersReducedMotion } from './motion';

/* Decorative 3D backdrop for dashboard heroes: glossy cherries, soft bokeh
   orbs and drifting petals in the brand colours.

   - three.js is imported dynamically, after the browser is idle, so it lives
     in its own chunk and never blocks first paint.
   - What the visitor sees before (and instead of) the scene is a static CSS
     gradient — also the final result without WebGL or with reduced motion.
   - The render loop only runs while the hero is on screen and the tab is
     visible, caps the pixel ratio at 1.5 and backs off on slow devices.
   - Everything created (geometries, materials, renderer, listeners) is
     released on unmount. */

const BASE_GRADIENT = [
  `radial-gradient(60% 90% at 88% 15%, rgba(242,89,114,0.38) 0%, transparent 70%)`,
  `radial-gradient(50% 70% at 70% 100%, rgba(232,169,61,0.18) 0%, transparent 70%)`,
  `linear-gradient(125deg, ${orchard.night} 0%, #3A0F25 48%, ${orchard.cherryDeep} 100%)`,
].join(', ');

function hasWebGL() {
  try {
    const c = document.createElement('canvas');
    const gl = c.getContext('webgl2') || c.getContext('webgl');
    if (!gl) return false;
    const lose = gl.getExtension('WEBGL_lose_context');
    if (lose) lose.loseContext();
    return true;
  } catch (_) {
    return false;
  }
}

const rand = (a, b) => a + Math.random() * (b - a);

function mountScene(THREE, host, { density }) {
  const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: dpr < 1.25,
      powerPreference: 'low-power',
    });
  } catch (_) {
    return null;
  }
  renderer.setPixelRatio(dpr);
  renderer.setClearColor(0x000000, 0);
  const canvas = renderer.domElement;
  canvas.style.cssText =
    'position:absolute;inset:0;width:100%;height:100%;display:block;opacity:0;';
  host.appendChild(canvas);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 60);
  camera.position.z = 14;

  scene.add(new THREE.HemisphereLight(0xf7e3e7, 0x8c1230, 1.1));
  const key = new THREE.DirectionalLight(0xffffff, 1.7);
  key.position.set(3, 5, 7);
  scene.add(key);
  const rim = new THREE.PointLight(0xf25972, 60, 40);
  rim.position.set(-6, -3, 4);
  scene.add(rim);

  // Shared geometries / materials — tracked so they can be disposed
  const disposables = [];
  const track = (o) => {
    disposables.push(o);
    return o;
  };
  const sphereGeo = track(new THREE.SphereGeometry(0.5, 18, 14));
  const stemGeo = track(new THREE.CylinderGeometry(0.03, 0.04, 1, 5));
  const leafGeo = track(new THREE.SphereGeometry(0.5, 10, 6));
  const phong = (color, shininess = 90) =>
    track(
      new THREE.MeshPhongMaterial({
        color,
        shininess,
        specular: 0x886677,
      })
    );
  const cherryMats = [
    phong(orchard.cherryBright),
    phong(orchard.cherry),
    phong('#FF7A8E'),
  ];
  const stemMat = phong('#6B8F4E', 20);
  const leafMat = phong(orchard.leafBright, 30);

  const small = host.clientWidth < 600;
  const cherryCount = Math.round((small ? 6 : 10) * density);
  const orbCount = Math.round((small ? 4 : 7) * density);
  const petalCount = Math.round((small ? 36 : 70) * density);

  // Items are laid out in normalised screen space (u, v in -1..1) and mapped
  // to world space on resize
  const items = [];

  const buildCherry = () => {
    const g = new THREE.Group();
    const mat = cherryMats[Math.floor(Math.random() * cherryMats.length)];
    const a = new THREE.Mesh(sphereGeo, mat);
    a.position.set(-0.55, -0.85, 0);
    const b = new THREE.Mesh(sphereGeo, mat);
    b.position.set(0.6, -0.75, 0.1);
    b.scale.setScalar(0.92);
    const top = { x: 0, y: 0.75 };
    const stem = (from, scale) => {
      const dx = top.x - from.x;
      const dy = top.y - from.y;
      const m = new THREE.Mesh(stemGeo, stemMat);
      m.scale.y = Math.hypot(dx, dy) - scale * 0.1;
      m.position.set((top.x + from.x) / 2, (top.y + from.y) / 2 + 0.1, 0);
      m.rotation.z = Math.atan2(-dx, dy);
      return m;
    };
    const leaf = new THREE.Mesh(leafGeo, leafMat);
    leaf.scale.set(1.1, 0.1, 0.55);
    leaf.position.set(0.42, 0.78, 0);
    leaf.rotation.z = 0.5;
    g.add(a, b, stem(a.position, 0.5), stem(b.position, 0.46), leaf);
    return g;
  };

  for (let i = 0; i < cherryCount; i++) {
    const mesh = buildCherry();
    const s = rand(0.55, 1.05);
    mesh.scale.setScalar(s);
    scene.add(mesh);
    items.push({
      mesh,
      kind: 'cherry',
      u: rand(-0.7, 1),
      v: rand(-0.9, 0.9),
      z: rand(-4, 2.5),
      amp: rand(0.15, 0.4),
      speed: rand(0.35, 0.8),
      phase: rand(0, Math.PI * 2),
      spin: rand(-0.25, 0.25),
      baseS: s,
    });
  }

  const orbColors = [orchard.honey, orchard.blossom, orchard.cherryBright];
  for (let i = 0; i < orbCount; i++) {
    const mat = track(
      new THREE.MeshBasicMaterial({
        color: orbColors[i % orbColors.length],
        transparent: true,
        opacity: rand(0.1, 0.22),
        depthWrite: false,
      })
    );
    const mesh = new THREE.Mesh(sphereGeo, mat);
    const s = rand(0.9, 2.2);
    mesh.scale.setScalar(s);
    scene.add(mesh);
    items.push({
      mesh,
      kind: 'orb',
      u: rand(-1, 1),
      v: rand(-1, 1),
      z: rand(-9, -4),
      amp: rand(0.3, 0.7),
      speed: rand(0.15, 0.4),
      phase: rand(0, Math.PI * 2),
      spin: 0,
      baseS: s,
    });
  }

  // Petals / dust: one Points object, slowly rising and wrapping
  const petalPos = new Float32Array(petalCount * 3);
  const petalSeed = [];
  for (let i = 0; i < petalCount; i++) {
    petalSeed.push({
      x: rand(-1, 1),
      y: rand(-1, 1),
      z: rand(-6, 3),
      sp: rand(0.012, 0.035),
      sway: rand(0.2, 0.8),
      ph: rand(0, 6.28),
    });
  }
  const petalGeo = track(new THREE.BufferGeometry());
  petalGeo.setAttribute('position', new THREE.BufferAttribute(petalPos, 3));
  const petalMat = track(
    new THREE.PointsMaterial({
      color: orchard.blossom,
      size: small ? 0.09 : 0.075,
      transparent: true,
      opacity: 0.7,
      depthWrite: false,
      sizeAttenuation: true,
    })
  );
  const petals = new THREE.Points(petalGeo, petalMat);
  petals.frustumCulled = false;
  scene.add(petals);

  const world = { halfW: 8, halfH: 5 };
  const pointer = { x: 0, y: 0, tx: 0, ty: 0 };
  let scrollOffset = 0;
  let scrollTarget = 0;
  let width = 0;
  let height = 0;

  const resize = () => {
    const w = host.clientWidth;
    const h = host.clientHeight;
    if (!w || !h) return;
    width = w;
    height = h;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    // Narrow screens: pull the camera back so cherries stay on screen
    camera.position.z = w < 600 ? 17 : 14;
    camera.updateProjectionMatrix();
    world.halfH = Math.tan((camera.fov * Math.PI) / 360) * camera.position.z;
    world.halfW = world.halfH * camera.aspect;
  };

  let running = false;
  let rafId = 0;
  let last = 0;
  let slowFrames = 0;
  let quality = 0;
  let stopped = false;

  const frame = (now) => {
    rafId = 0;
    if (!running || stopped) return;
    rafId = requestAnimationFrame(frame);
    const dtMs = now - last;
    if (dtMs < 15) return; // ~60 fps ceiling on high-refresh screens
    last = now;
    const dt = Math.min(dtMs, 64) / 1000;
    const t = now / 1000;

    // Back off on slow devices: first drop resolution, then freeze
    slowFrames = dtMs > 45 ? slowFrames + 1 : Math.max(0, slowFrames - 1);
    if (slowFrames > 40) {
      slowFrames = 0;
      quality += 1;
      if (quality === 1) {
        renderer.setPixelRatio(1);
        resize();
      } else {
        stopped = true;
        return;
      }
    }

    pointer.x += (pointer.tx - pointer.x) * Math.min(1, dt * 3);
    pointer.y += (pointer.ty - pointer.y) * Math.min(1, dt * 3);
    scrollOffset += (scrollTarget - scrollOffset) * Math.min(1, dt * 5);

    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      const depth = 1 + it.z * 0.07; // nearer items move more
      const px = pointer.x * 0.9 * depth;
      const py = pointer.y * 0.6 * depth;
      it.mesh.position.x = it.u * world.halfW * 0.95 + px;
      it.mesh.position.y =
        it.v * world.halfH * 0.9 +
        Math.sin(t * it.speed + it.phase) * it.amp +
        py +
        scrollOffset * depth;
      it.mesh.position.z = it.z;
      if (it.kind === 'cherry') {
        it.mesh.rotation.y += it.spin * dt;
        it.mesh.rotation.z = Math.sin(t * 0.5 + it.phase) * 0.22;
        it.mesh.rotation.x = Math.cos(t * 0.4 + it.phase) * 0.12;
      }
    }

    const p = petalGeo.attributes.position;
    for (let i = 0; i < petalCount; i++) {
      const s = petalSeed[i];
      s.y += s.sp * dt * 6;
      if (s.y > 1.1) s.y = -1.1;
      p.array[i * 3] =
        s.x * world.halfW +
        Math.sin(t * s.sway + s.ph) * 0.25 +
        pointer.x * 0.4;
      p.array[i * 3 + 1] = s.y * world.halfH + scrollOffset * 0.8;
      p.array[i * 3 + 2] = s.z;
    }
    p.needsUpdate = true;

    renderer.render(scene, camera);
  };

  const renderOnce = () => {
    // Lay items out and draw a still frame (used before the loop starts and
    // when the loop is paused)
    if (!width) resize();
    last = performance.now() - 20;
    const prev = running;
    running = true;
    frame(performance.now());
    if (rafId) cancelAnimationFrame(rafId);
    rafId = 0;
    running = prev;
  };

  let visible = true;
  const sync = () => {
    const should = visible && !document.hidden && !stopped;
    if (should && !running) {
      running = true;
      last = performance.now();
      rafId = requestAnimationFrame(frame);
    } else if (!should && running) {
      running = false;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = 0;
    }
  };

  const io =
    typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver(
          (entries) => {
            visible = entries[entries.length - 1].isIntersecting;
            sync();
          },
          { threshold: 0 }
        )
      : null;
  if (io) io.observe(host);

  const ro =
    typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => {
          resize();
          if (!running) renderOnce();
        })
      : null;
  if (ro) ro.observe(host);

  const onPointer = (e) => {
    if (!visible || !width) return;
    const r = host.getBoundingClientRect();
    pointer.tx = ((e.clientX - r.left) / r.width - 0.5) * 2;
    pointer.ty = -((e.clientY - r.top) / r.height - 0.5) * 2;
  };
  const onScroll = () => {
    const r = host.getBoundingClientRect();
    scrollTarget = Math.max(-3, Math.min(3, -r.top * 0.012));
  };
  const onVisibility = () => sync();
  const onLost = (e) => {
    e.preventDefault();
    stopped = true;
    sync();
    canvas.style.display = 'none';
  };

  window.addEventListener('pointermove', onPointer, { passive: true });
  window.addEventListener('scroll', onScroll, { passive: true });
  document.addEventListener('visibilitychange', onVisibility);
  canvas.addEventListener('webglcontextlost', onLost);

  resize();
  onScroll();
  renderOnce();
  gsap.to(canvas, { opacity: 1, duration: 1.2, ease: 'power2.out' });
  sync();

  return () => {
    stopped = true;
    running = false;
    if (rafId) cancelAnimationFrame(rafId);
    window.removeEventListener('pointermove', onPointer);
    window.removeEventListener('scroll', onScroll);
    document.removeEventListener('visibilitychange', onVisibility);
    canvas.removeEventListener('webglcontextlost', onLost);
    if (io) io.disconnect();
    if (ro) ro.disconnect();
    gsap.killTweensOf(canvas);
    disposables.forEach((d) => d.dispose());
    renderer.dispose();
    try {
      renderer.forceContextLoss();
    } catch (_) {}
    if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
  };
}

export default function HeroScene({ density = 1, style }) {
  const hostRef = useRef(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || prefersReducedMotion() || !hasWebGL()) return undefined;
    let cancelled = false;
    let teardown = null;

    const start = () => {
      import('three')
        .then((THREE) => {
          if (cancelled || !host.isConnected) return;
          teardown = mountScene(THREE, host, { density });
        })
        .catch(() => {
          // Chunk failed to load: the gradient stays as the hero background
        });
    };

    let idleId;
    let timeoutId;
    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(start, { timeout: 1500 });
    } else {
      timeoutId = window.setTimeout(start, 350);
    }

    return () => {
      cancelled = true;
      if (idleId !== undefined && window.cancelIdleCallback) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
      if (teardown) teardown();
    };
  }, [density]);

  return (
    <div
      ref={hostRef}
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        background: BASE_GRADIENT,
        ...style,
      }}
    />
  );
}
